<script setup lang="ts">
import SearchTemplate from '@/components/Search/SearchTemplate.vue';
import { SearchExtraContent } from '@/models/search-extra-content.model';
import { SearchResult } from '@/models/search.model';
import CmsDataProvider, { CMS_DATA_ID } from "@/providers/cms.provider";
import kenmerkRepository, { Kenmerk } from '@/repositories/kenmerk.repository';
import { LocationData } from '@/repositories/places.repository';
import trainerRepository, { ITrainerSearchParams, TrainerSearchItem } from '@/repositories/trainer.repository';
import { LngLat } from 'mapbox-gl';
import { computed, onBeforeMount, reactive, watch } from 'vue';
import OrderByDropdown, { SortByOption } from '../ActivitySearch/components/OrderByDropdown.vue';
import { useProfileStore } from '@/stores/profile.store';
import { usePlatformStore } from '@/stores/platform.store';
import { useFilters } from '@/helpers/filter.helper';
import { getCurrentLocation } from '@/helpers/location.helper';
import { notify } from '@kyvg/vue3-notification';
import { generateFacetList } from '@/helpers/facet.helper';
import { useDebounceFn } from '@vueuse/core';
import { addToDataLayer } from '@/services/gtm.service';
import LocationSearch from '@/components/Search/LocationSearch.vue';
import DistanceFilter from '../ActivitySearch/components/DistanceFilter.vue';
import FacetFilter from '@/components/ActivitySearchFilters/FacetFilter.vue';
import ActiveFilters from '../ActivitySearch/components/ActiveFilters.vue';
import TrainerDetail from '@/components/Trainers/TrainerDetail.vue';
import TrainerSearchResult from '@/components/Trainers/TrainerSearchResult.vue';
import Sidebar from '@/components/Sidebar.vue';
import { HiddenElements, HiddenFilters } from '@/view-models/filters.model';
import SearchSwapper from '@/components/Search/SearchSwapper.vue';
import StylessButton from '@/components/StylessButton.vue';

const cmsData = CmsDataProvider.get<{
    title?: string,
    searchSubtext?: string,

    clubSearchUrl: string,
    activiteitenSearchUrl: string,
    trainerSearchUrl: string,

    activitiesSingular: string,
    activitiesPlural: string,
    clubsSingular: string,
    clubsPlural: string,
    trainerSingular: string,
    trainerPlural: string,
    hiddenFilters: HiddenFilters,
    hiddenElements: HiddenElements,
    extraContent: SearchExtraContent[]
}>(CMS_DATA_ID.TRAINER_SEARCH);

const platformStore = usePlatformStore();
const profileStore = useProfileStore();

const sortOptions: SortByOption[] = [
    { label: "Naam (a-z)", value: "Naam" },
    { label: "Naam (z-a)", value: "Naam", isDesc: true },
    { label: "Club (a-z)", value: "SportaanbiederNaam" },
    { label: "Club (z-a)", value: "SportaanbiederNaam", isDesc: true },
];

const currentSort = computed(() => {
    const foundSortOption = sortOptions.find(option =>
        option.value === searchFilter.filters.OrderBy && option.isDesc == searchFilter.filters.Desc);

    return foundSortOption ?? sortOptions[0];
});

const searchFilter = useFilters<ITrainerSearchParams>({
    type: 'trainer',
    platform_facet: platformStore.platformId,
    facetlist: generateFacetList(cmsData.hiddenFilters, platformStore.isGolfPlatform),
    pageSize: 20,
    offset: 0,
    OrderBy: 'Naam',
}, () => {
    debouncedSearch();
});

const initialSearchResults: SearchResult<TrainerSearchItem, TrainerSearchItem> = {
    Count: 0,
    Items: [],
    Query: null,
    Facets: [],
    GroupCount: null,
    Groups: []
};

const state = reactive<{
    isLoading: boolean,
    searchResults: SearchResult<TrainerSearchItem, TrainerSearchItem>,
    currentPage: number,
    showTrainerSidebar: boolean,
    selectedTrainer?: TrainerSearchItem,
    location?: LngLat,
    selectedDistance?: { label: string, value: number },
    kenmerken: { facet: string, kenmerken: Kenmerk[] }[],
    allKenmerken: Kenmerk[];
    lastFacetChanged?: string,
    selectedLocation?: LocationData,
}>({
    isLoading: true,
    searchResults: initialSearchResults,
    showTrainerSidebar: false,
    currentPage: 0,
    kenmerken: [],
    allKenmerken: [],
});

const trainerSingular = computed(() => {
    return cmsData.trainerSingular ? cmsData.trainerSingular : "trainer"
})

const trainerPlural = computed(() => {
    return cmsData.trainerPlural ? cmsData.trainerPlural : "trainers"
})


const tryDistanceSearch = () => {
    if (state.selectedDistance?.value && state.location?.lat && state.location?.lng) {
        debouncedSearch();
    }
}

const toggleDistanceSortOption = (lat?: number, lng?: number) => {
    if (lat && lng) {
        const sortOptionIndex = sortOptions.findIndex(option => option.value === "Distance,Naam");
        if (sortOptionIndex === -1) {
            sortOptions.push({ label: "Afstand", value: "Distance,Naam" });
            if (!state.selectedDistance) state.selectedDistance = { label: '25 KM', value: 25 };
            searchFilter.setFilter("OrderBy", "Distance,Naam");
        }
    } else {
        const index = sortOptions.findIndex(option => option.value === "Distance,Naam");
        if (index > -1) sortOptions.splice(index, 1);
    }
}

const locateUser = async () => {
    await getCurrentLocation()
        .then(position => {
            state.location = new LngLat(position.coords.longitude, position.coords.latitude)
            toggleDistanceSortOption(position.coords.latitude, position.coords.longitude);
        })
        .catch(error => {
            if (error.code === 1) {
                notify({
                    type: 'warn',
                    title: 'Geen locatie toestemming',
                    text: 'Zonder toestemming is het niet mogelijk om op locatie te zoeken',
                    duration: -1
                })
            }
            else {
                notify({
                    type: 'error',
                    title: 'Er is een fout opgetreden bij het ophalen jouw locatie',
                    duration: 20000
                })
            }
        });
}

const onShowMore = () => {
    debouncedSearch(true);
}

const onSortChange = (sortBy: SortByOption) => {
    searchFilter.setFilter("OrderBy", sortBy.value);
    searchFilter.setFilter("Desc", sortBy.isDesc);
}

const onQueryChange = () => {
    searchFilter.setFilter("query", searchFilter.filters.query);
}

const onFacetChange = (facet: string, selectedFacets?: string[] | number[], activatieFacets?: string[] | number[]) => {
    if (!selectedFacets) {
        state.lastFacetChanged = undefined;
    } else {
        state.lastFacetChanged = selectedFacets.length > 0 ? facet : undefined;
    }

    searchFilter.setFilter(facet as keyof ITrainerSearchParams, selectedFacets ?? activatieFacets);
}

const onDistanceChange = (distance?: { label: string, value: number }) => {
    state.selectedDistance = distance;
    if (distance?.value && state.location) {
        searchFilter.setFilter("distanceInKms", distance.value);
    }
    tryDistanceSearch();
}

const onLocationSelected = (data: LocationData) => {
    addToDataLayer('search', {
        'search_term': data.Naam ?? ''
    });

    if (!state.selectedDistance) {
        state.selectedDistance = { label: '25 KM', value: 25 }
    }
    state.selectedLocation = data;
    state.location = new LngLat(data.Longitude, data.Latitude);

    searchFilter.setFilter("centerLat", state.location.lat);
    searchFilter.setFilter("centerLon", state.location.lng);
    searchFilter.setFilter("distanceInKms", state.selectedDistance.value);
    searchFilter.setFilter("locationName" as any, data.Naam);

    tryDistanceSearch();
}

const getFacetsOfGroup = (facetName: keyof ITrainerSearchParams) => {
    return state.searchResults.Facets?.find(facet => facet.GroupName === facetName)?.Facets ?? [];
}

const clearDistance = () => {
    state.selectedDistance = undefined;
    state.selectedLocation = undefined;
    state.location = undefined;
    searchFilter.setFilter("OrderBy", "Naam");
    searchFilter.setFilter("locationName" as any, undefined);
}

const getKenmerken = async () => {
    state.allKenmerken = await kenmerkRepository.getKenmerken();

    if (!cmsData.hiddenFilters.hideOpleidingFilter) {
        const kenmerken = await kenmerkRepository.getKenmerkenByCategorie(37);
        state.kenmerken.push({ facet: "opleidingen_facet", kenmerken });
    }
}

const getKenmerk = (facetName: string) => {
    return state.kenmerken.find(kenmerk => kenmerk.facet === facetName)?.kenmerken;
}

const rebuildFilters = () => {

    const searchParams = new URL(window.location.href).searchParams;
    const searchOnLocation = searchParams.get("searchOnLocation") === "true";
    const allEntries = Array.from(searchParams.entries());
    const locationDistanceFilters: { centerLon?: number, centerLat?: number, distanceInKms?: number } = {};

    for (const entry of allEntries) {
        const key = entry[0] as keyof ITrainerSearchParams;
        const value = entry[1];

        if (!value) continue;

        switch (key) {
            // string[]    
            case "opleidingen_facet":
            case "plaatsen_facet":
            case "wijken_facet":
            case "wijkengemeenten_facet":
                if (searchFilter.filters[key] === undefined) {
                    searchFilter.filters[key] = [];
                }
                searchFilter.filters[key]!.push(value);
                break;
            // string    
            case "query":
            case "OrderBy":
                Object.assign(searchFilter.filters, { [key]: value });
                break;
            case "Desc":
                searchFilter.filters.Desc = Boolean(value);
                break;
            case "centerLon":
                locationDistanceFilters.centerLon = Number(value);
                break;
            case "centerLat":
                locationDistanceFilters.centerLat = Number(value);
                break;
            case "distanceInKms":
                locationDistanceFilters.distanceInKms = Number(value);
                break;
            default:
                break;
        }
    }

    if (locationDistanceFilters.centerLat && locationDistanceFilters.centerLon && locationDistanceFilters.distanceInKms) {
        Object.assign(searchFilter.filters, {
            ...locationDistanceFilters
        });
        state.location = new LngLat(locationDistanceFilters.centerLon!, locationDistanceFilters.centerLat!);
        state.selectedDistance = { label: `${locationDistanceFilters.distanceInKms} KM`, value: locationDistanceFilters.distanceInKms! };
        const locationName = searchParams.get('locationName');
        if (locationName) {
            state.selectedLocation = {
                Id: 0,
                Naam: locationName,
                Latitude: locationDistanceFilters.centerLat!,
                Longitude: locationDistanceFilters.centerLon!
            };
        }
    } else if (searchOnLocation) {
        locateUser();
        if (!state.selectedDistance) {
            state.selectedDistance = { label: '50 KM', value: 50 }
        }
    } else if (searchFilter.filters.OrderBy === "Distance,StartDatum") {
        searchFilter.setFilter("OrderBy", sortOptions[0].value);
    }
}

const debouncedSearch = useDebounceFn(async (newPage = false) => {
    state.isLoading = true;

    if (newPage) {
        state.currentPage++;
    } else {
        state.currentPage = 0;
    }

    if (state.selectedDistance?.value && state.location?.lat && state.location?.lng) {
        searchFilter.filters.centerLat = state.location.lat;
        searchFilter.filters.centerLon = state.location.lng;
        searchFilter.filters.distanceInKms = state.selectedDistance.value;
    } else if (currentSort.value.value === "Distance,StartDatum") {
        searchFilter.filters.OrderBy = sortOptions[0].value;
        searchFilter.filters.Desc = sortOptions[0].isDesc;
    }


    searchFilter.filters.offset = state.currentPage * searchFilter.filters.pageSize;

    await trainerRepository.search(searchFilter.filters)
        .then(searchResults => {
            if (newPage) {
                state.searchResults.Items.push(...searchResults.Items);
            } else {
                state.searchResults = searchResults;
            }

            return searchResults;
        })
        .catch(() => {
            notify({
                type: 'error',
                title: 'Er is een fout opgetreden bij het ophalen van de resultaten',
                duration: 20000
            });
            if (newPage && state.currentPage > 0) state.currentPage--;
        });

    state.isLoading = false;
}, 500);

const openTrainerSidebar = (trainer: TrainerSearchItem) => {
    state.selectedTrainer = trainer;
    state.showTrainerSidebar = true;
};

const closeTrainerSidebar = () => {
    state.selectedTrainer = undefined;
    state.showTrainerSidebar = false;
}

const woonplaats = computed(() => {
    return [state.selectedTrainer?.LocatieItem?.Adres, state.selectedTrainer?.LocatieItem?.Woonplaats]
        .filter(v => v && v.trim() !== "")
        .join(", ");
})


watch(() => [searchFilter.filters.centerLat, searchFilter.filters.centerLon], ([newLat, newLon]) => {
    toggleDistanceSortOption(newLat, newLon);
});

onBeforeMount(async () => {
    rebuildFilters();
    await getKenmerken();
    await platformStore.ensurePlatform();
    await profileStore.ensureProfile();
    await debouncedSearch();
})

</script>

<template>
    <SearchTemplate :search-item-name-singular="cmsData.trainerSingular"
        :search-item-name-plural="cmsData.trainerPlural" :search-results="state.searchResults"
        :is-loading="state.isLoading" @show-more="onShowMore" :title="cmsData.title"
        :search-subtext="cmsData.searchSubtext" :extra-content="cmsData.extraContent"
        more-result-button-text="Laad meer">
        <template #searchHeader v-if="!cmsData.hiddenElements?.hideSearchHeader">
            <div v-if="!cmsData.hiddenFilters.hideSearchBox" class="search-input-wrapper">
                <label for="zoekterm-input" class="sr-only">Zoekterm</label>
                <input id="zoekterm-input" class="form-control" placeholder="Waar ben je naar op zoek?"
                    v-model="searchFilter.filters.query" @change="onQueryChange()" />
                <span class="icon icon-search"></span>
            </div>
        </template>

        <template #filters>
            <LocationSearch v-if="!cmsData.hiddenFilters.hideSearchOnLocation"
                :selectedLocation="state.selectedLocation"
                :hide-location-attempt="cmsData.hiddenElements?.hideLocationAttempt" @select="onLocationSelected"
                @location-attempt="locateUser" />

            <DistanceFilter v-if="!cmsData.hiddenFilters.hideDistanceFilter" :coordinates="state.location"
                :current-value="searchFilter.filters.distanceInKms" @change="onDistanceChange"
                @location-attempt="locateUser" />

            <hr />

            <FacetFilter v-if="!cmsData.hiddenFilters.hideOpleidingFilter" facet-name="opleidingen_facet"
                :facets="getFacetsOfGroup('opleidingen_facet')" :active-facets="searchFilter.filters.opleidingen_facet"
                name="Opleiding" button-name="opleidingen" :last-facet-changed="state.lastFacetChanged"
                @change="onFacetChange" :kenmerken="getKenmerk('opleidingen_facet')" />

            <FacetFilter v-if="!cmsData.hiddenFilters.hideLocationFilter" facet-name="plaatsen_facet"
                :facets="getFacetsOfGroup('plaatsen_facet')" :active-facets="searchFilter.filters.plaatsen_facet"
                name="Plaats" button-name="plaatsen" :last-facet-changed="state.lastFacetChanged"
                @change="onFacetChange" />

            <FacetFilter v-if="!cmsData.hiddenFilters.hideWijkFilter" facet-name="wijken_facet"
                :facets="getFacetsOfGroup('wijken_facet')" :active-facets="searchFilter.filters.wijken_facet"
                name="Wijk" :last-facet-changed="state.lastFacetChanged" @change="onFacetChange" />

            <FacetFilter v-if="!cmsData.hiddenFilters.hideWijkGemeenteFilter" facet-name="wijkengemeenten_facet"
                :facets="getFacetsOfGroup('wijkengemeenten_facet')"
                :active-facets="searchFilter.filters.wijkengemeenten_facet" name="Wijk"
                :last-facet-changed="state.lastFacetChanged" @change="onFacetChange" />

        </template>

        <template #resultHeader="{ toggleSidebarFilter }">
            <ActiveFilters :search-filter="searchFilter" :day-parts="[]" :kenmerken="state.kenmerken"
                :selected-location="state.selectedLocation" @clear-distance="clearDistance" @change="debouncedSearch" />

            <div class="ordering">
                <SearchSwapper active="trainer" :activities-link="cmsData.activiteitenSearchUrl"
                    :activities-plural="cmsData.activitiesPlural" :clubs-link="cmsData.clubSearchUrl"
                    :clubs-plural="cmsData.clubsPlural" :trainer-link="cmsData.trainerSearchUrl"
                    :trainer-plural="cmsData.trainerPlural" :center-lat="searchFilter.filters.centerLat"
                    :center-lon="searchFilter.filters.centerLon" :distance-in-kms="searchFilter.filters.distanceInKms"
                    :location-name="state.selectedLocation?.Naam" />

                <OrderByDropdown :sort-options="sortOptions" :currentSort="currentSort" @change="onSortChange" />

                <StylessButton class="d-md-none" @click.prevent="toggleSidebarFilter" data-sidebar-filtering-toggle>
                    <span class="icon-sliders" />
                    Filter
                </StylessButton>
            </div>
        </template>

        <template #resultItem="slotProps: { item: SearchEntity<TrainerSearchItem> }">
            <slot name="results">
                <TrainerSearchResult :trainer-plural="trainerPlural" :trainer-singular="trainerSingular"
                    :kenmerken="state.allKenmerken" :entity="slotProps.item.Entity" @selected="openTrainerSidebar" />
            </slot>
        </template>
    </SearchTemplate>

    <Sidebar :is-open="state.showTrainerSidebar" @close="closeTrainerSidebar">
        <TrainerDetail v-if="state.selectedTrainer" :trainer="state.selectedTrainer" :kenmerken="state.allKenmerken"
            :club="state.selectedTrainer.SportaanbiederItem" :woonplaats="woonplaats"
            :trainer-singular="cmsData.trainerSingular" :clubs-singular="cmsData.clubsSingular"
            @close="closeTrainerSidebar" />
    </Sidebar>
</template>